<template>
  <div class="pb-24">
  <div>
    <div class="p-6 pb-0">
      <BreadCrumbs :navigation="navigation" />
    </div>

    <div class="p-6">
      <!-- Document Container -->
      <div class="bg-white rounded-lg shadow-lg overflow-hidden">

        <!-- Title Bar -->
        <div class="bg-blue-600 text-white p-4">
          <div class="flex justify-between items-center">
            <div class="flex items-center gap-3">
              <div class="w-10 h-10 bg-white rounded-full flex items-center justify-center">
                <v-icon name="co-truck" class="text-blue-600" scale="1.5" />
              </div>
              <h1 class="text-xl font-bold">{{ $t('pv.generazione.header_title') }}</h1>
            </div>
            <div class="text-right text-sm">
              <div class="font-semibold">{{ $t('pv.generazione.travel_of') }} {{ formatDisplayDate(dateFrom) }}</div>
              <div v-if="dateTo && formatDate(dateFrom) !== formatDate(dateTo)" class="text-xs opacity-80">{{ $t('pv.generazione.travel_until') }} {{
                formatDisplayDate(dateTo) }}</div>
            </div>
          </div>
        </div>

        <!-- Carrier Info Header -->
        <div v-if="hasSearched && carrierInfo" class="border-b-2 border-gray-300 p-4">
          <div class="grid grid-cols-3 gap-4">
            <!-- Carrier Info Box -->
            <div class="col-span-1    p-3">
              <p class="font-bold text-lg text-gray-900">{{ carrierInfo.CompanyName }}</p>
              <p class="text-sm text-gray-600">{{ carrierInfo.Address }}</p>
              <p class="text-sm text-gray-600">{{ carrierInfo.City }}</p>
              <div class="mt-3 pt-2 border-t border-gray-300">
                <p class="text-sm">
                  <span class="text-gray-500">{{ $t('pv.generazione.driver_label') }} </span>
                  <span class="font-medium"> {{ carrierInfo.Carrier }}</span>
                </p>
                <p class="text-sm">
                  <span class="text-gray-500">{{ $t('pv.generazione.notes_label') }} </span>
                  <span class="font-medium"> {{ carrierInfo.Notes }}</span>
                </p>
              </div>

              <div v-if="specifiche_veicoli" class="mt-2">
                <div class="p-2 bg-blue-50 border border-blue-200 rounded-lg">
                  <label class="block text-xs font-bold text-blue-700 uppercase mb-1">
                    {{ $t('pv.generazione.vehicle_selection') }}
                  </label>

                  <select
                    v-model="selectedVehicle"
                    class="w-full p-1 text-xs border-gray-300 rounded shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  >
                    <option value="">
                      {{ $t('pv.generazione.select_vehicle') }}
                    </option>

                    <option
                      v-for="vehicle in veicoli"
                      :key="vehicle.id"
                      :value="vehicle.id"
                    >
                      {{ vehicle.name }}
                    </option>
                  </select>
                </div>
              </div>
            </div>
            
            
            <!-- KM and Actions -->
            <div class="space-y-3">
              <div class="grid grid-cols-2 gap-2">
                <div class="border border-gray-300 py-1 px-2 bg-gray-50">
                  <label class="text-[10px] text-gray-500 block uppercase font-bold">{{ $t('pv.generazione.total_distance') }}</label>
                  <div class="border-b-2 border-blue-400 min-h-[20px] mt-1 font-bold text-blue-700">
                    {{ routeData?.distance || '---' }}
                  </div>
                  <label class="text-[10px] text-gray-500 block uppercase font-bold">{{ $t('pv.generazione.expected_time') }}</label>
                  <div class="border-b-2 border-blue-400 min-h-[20px] mt-1 font-bold text-blue-700">
                    {{ routeData?.duration || '---' }}
                  </div>
                </div>
                <div class="border border-gray-300 py-1 px-2 bg-gray-50">
                  <label class="text-[10px] text-gray-500 block uppercase font-bold mb-1">{{ $t('pv.generazione.route_configuration') }}</label>
                  <div class="flex flex-wrap gap-1">
                    <span v-if="avoidTolls" class="bg-gray-700 text-white px-1.5 py-0.5 rounded text-[8px] font-black uppercase shadow-sm">{{ $t('pv.generazione.tag_avoid_tolls') }}</span>
                    <span v-if="avoidHighways" class="bg-gray-700 text-white px-1.5 py-0.5 rounded text-[8px] font-black uppercase shadow-sm">{{ $t('pv.generazione.tag_avoid_highways') }}</span>
                    <span v-if="avoidFerries" class="bg-gray-700 text-white px-1.5 py-0.5 rounded text-[8px] font-black uppercase shadow-sm">{{ $t('pv.generazione.tag_avoid_ferries') }}</span>
                    <span v-if="!avoidTolls" class="bg-green-700 text-white px-1.5 py-0.5 rounded text-[8px] font-black uppercase shadow-sm">{{ $t('pv.generazione.tag_tolls_allowed') }}</span>
                    <span v-if="!avoidHighways" class="bg-green-700 text-white px-1.5 py-0.5 rounded text-[8px] font-black uppercase shadow-sm">{{ $t('pv.generazione.tag_highways_allowed') }}</span>
                    <span v-if="!avoidFerries" class="bg-green-700 text-white px-1.5 py-0.5 rounded text-[8px] font-black uppercase shadow-sm">{{ $t('pv.generazione.tag_ferries_allowed') }}</span>
                    
                  </div>
                </div>
              </div>
              <div class="flex flex-wrap gap-2">
                <button v-if="trips.length > 0" @click="openRouteEditor"
                  class="flex-1 px-3 py-2 bg-green-600 text-white hover:bg-green-700 rounded text-xs font-medium flex items-center justify-center gap-1">
                  <v-icon name="co-map" scale="0.9" /> {{ $t('pv.generazione.btn_route') }}
                </button>
                <button @click="printTable" :disabled="!lastSavedId || isLoading"
                  :class="{'opacity-50 cursor-not-allowed': !lastSavedId}"
                  class="flex-1 px-3 py-2 bg-gray-600 text-white hover:bg-gray-700 rounded text-xs font-medium flex items-center justify-center gap-1">
                  <v-icon name="co-print" scale="0.9" /> {{ $t('pv.generazione.btn_print') }}
                </button>
                <button @click="esportaCsv" :disabled="!lastSavedId || isLoading"
                  :class="{'opacity-50 cursor-not-allowed': !lastSavedId}"
                  class="flex-1 px-3 py-2 bg-orange-600 text-white hover:bg-orange-700 rounded text-xs font-medium flex items-center justify-center gap-1">
                  <v-icon name="bi-download" scale="0.9" /> {{ $t('pv.generazione.btn_export_excel') }}
                </button>

                
              </div>
              <div class="flex flex-wrap gap-2">
                <button 
                  v-if="!hasPreloadedTrips"
                  @click="showSelectionModal = true"
                  class="flex-1 px-3 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded text-xs font-medium flex items-center justify-center gap-1">
                  <v-icon :name="lastSavedId ? 'hi-plus' : 'hi-refresh'" scale="0.9" /> {{ lastSavedId ? $t('pv.generazione.btn_new') : $t('pv.generazione.btn_reload') }}
                </button>
              </div>
              <div class="grid grid-cols-2 gap-2 mt-2">
                <div class="p-2 bg-blue-50 border border-blue-200 rounded-lg">
                  <label class="block text-xs font-bold text-blue-700 uppercase mb-1">{{ $t('pv.generazione.departure_point') }}</label>
                  <select
                      v-model="selectedOrigin"
                      class="w-full p-1 text-xs border-gray-300 rounded shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  >
                      <option v-for="(opt, idx) in originOptions" :key="idx" :value="opt">
                          {{ opt.label }} ({{ opt.address }})
                      </option>
                  </select>
                  <button
                      @click="openCustomOriginInput"
                      class="mt-1 text-xs text-blue-600 hover:underline"
                      type="button"
                  >
                      {{ $t('pv.generazione.add_custom_address') }}
                  </button>
                  <Transition name="fade">
                    <div v-if="showCustomOriginInput" class="fixed inset-0 z-[200] flex items-center justify-center bg-black/30" @click.self="showCustomOriginInput = false">
                      <div class="bg-white rounded-lg shadow-xl p-4 w-96 mx-4 relative">
                        <label class="block text-xs font-bold text-blue-700 uppercase mb-2">{{ $t('pv.generazione.add_departure_address') }}</label>
                        <div class="relative">
                          <input
                              ref="customOriginInput"
                              type="text"
                              :placeholder="$t('pv.generazione.search_google_placeholder')"
                              class="w-full p-2 text-sm border border-gray-300 rounded shadow-sm focus:border-blue-500 focus:ring-blue-500"
                          />
                          <div v-if="originPredictions.length" class="absolute top-full left-0 right-0 border border-gray-300 rounded mt-1 max-h-56 overflow-y-auto bg-white z-50 shadow-lg">
                            <div 
                              v-for="pred in originPredictions" 
                              :key="pred.place_id"
                              @click="selectOriginPrediction(pred)"
                              class="p-3 hover:bg-blue-100 cursor-pointer text-sm border-b"
                            >
                              <div class="font-bold text-gray-900">{{ pred.structured_formatting?.main_text || pred.description }}</div>
                              <div class="text-gray-600 text-xs mt-1">{{ pred.structured_formatting?.secondary_text }}</div>
                            </div>
                          </div>
                        </div>
                        <div class="flex justify-end mt-3">
                          <button @click="showCustomOriginInput = false" class="text-xs text-gray-500 px-3 py-1 border border-gray-300 rounded hover:bg-gray-50" type="button">{{ $t('actions.cancel') }}</button>
                        </div>
                      </div>
                    </div>
                  </Transition>
                </div>
                <div class="p-2 bg-red-50 border border-red-200 rounded-lg">
                  <label class="block text-xs font-bold text-red-700 uppercase mb-1">{{ $t('pv.generazione.arrival_point') }}</label>
                  <select
                      v-model="selectedDestination"
                      class="w-full p-1 text-xs border-gray-300 rounded shadow-sm focus:border-red-500 focus:ring-red-500"
                  >
                      <option v-for="(opt, idx) in destinationOptions" :key="idx" :value="opt">
                          {{ opt.label }} ({{ opt.address }})
                      </option>
                  </select>
                  <button
                      @click="openCustomDestinationInput"
                      class="mt-1 text-xs text-red-600 hover:underline"
                      type="button"
                  >
                      {{ $t('pv.generazione.add_custom_address') }}
                  </button>
                  
                  <Transition name="fade">
                    <div v-if="showCustomDestinationInput" class="fixed inset-0 z-[200] flex items-center justify-center bg-black/30" @click.self="showCustomDestinationInput = false">
                      <div class="bg-white rounded-lg shadow-xl p-4 w-96 mx-4 relative">
                        <label class="block text-xs font-bold text-red-700 uppercase mb-2">{{ $t('pv.generazione.add_arrival_address') }}</label>
                        <div class="relative">
                          <input
                              ref="customDestinationInput"
                              type="text"
                              :placeholder="$t('pv.generazione.search_google_placeholder')"
                              class="w-full p-2 text-sm border border-gray-300 rounded shadow-sm focus:border-red-500 focus:ring-red-500"
                          />
                          <div v-if="destinationPredictions.length" class="absolute top-full left-0 right-0 border border-gray-300 rounded mt-1 max-h-56 overflow-y-auto bg-white z-50 shadow-lg">
                            <div 
                              v-for="(pred, idx) in destinationPredictions" 
                              :key="idx"
                              @click="selectDestinationPrediction(pred)"
                              class="p-3 hover:bg-red-100 cursor-pointer text-sm border-b"
                            >
                              <div class="font-bold text-gray-900">{{ pred.structured_formatting?.main_text || pred.description }}</div>
                              <div class="text-gray-600 text-xs mt-1">{{ pred.structured_formatting?.secondary_text }}</div>
                            </div>
                          </div>
                        </div>

                        <div class="flex justify-end mt-3">
                          <button @click="showCustomDestinationInput = false" class="text-xs text-gray-500 px-3 py-1 border border-gray-300 rounded hover:bg-gray-50" type="button">{{ $t('actions.cancel') }}</button>
                        </div>
                      </div>
                    </div>
                  </Transition>
                </div>
              </div>

              <div class="col-span-3 mt-2 p-2 bg-gray-50 border border-gray-200 rounded-lg">
                  <label class="block text-xs font-bold text-gray-700 uppercase mb-1">{{ $t('pv.generazione.route_name_label') }}</label>
                  <input 
                      v-model="travelName"
                      type="text"
                      :placeholder="$t('pv.generazione.route_name_placeholder')"
                      class="w-full p-1.5 text-xs border-gray-300 rounded shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
              </div>
              
            </div>
            <div>
              <!-- QR Code Container -->
              <div class="col-span-3 flex justify-center mt-4">
                <div class="p-3 bg-white border-2 border-gray-300 rounded-xl shadow-sm">
                  <div v-if="generatedMapsLink" class="w-full max-w-[250px] mx-auto">
                    <qrcode-vue 
                      :value="generatedMapsLink" 
                      :size="250"
                      level="M" 
                      render-as="svg"
                      class="w-full h-auto block" 
                    />
                  </div>
                  <div v-else class="w-36 h-36 flex items-center justify-center bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg">
                    <span class="text-[10px] text-gray-400 text-center px-2 font-bold uppercase">{{ $t('pv.generazione.qr_waiting') }}</span>
                  </div>
                </div>
              </div>
              <!-- Accorpamento Tappe Toggle -->
              <div class="col-span-3 flex justify-center mt-3">
                <label class="flex items-center cursor-pointer gap-2 bg-white border border-gray-300 px-4 py-2 rounded-xl shadow-sm hover:bg-gray-50 transition-all border-dashed">
                  <input 
                    type="checkbox" 
                    v-model="mergeSameAddress" 
                    class="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span class="text-[10px] font-black text-gray-700 uppercase tracking-wider">
                    {{ $t('pv.generazione.merge_same_address') }}
                  </span>
                </label>
              </div>

            </div>
          </div>
        </div>

        <!-- Loading -->
        <div v-if="isLoading" class="flex items-center justify-center py-12">
          <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span class="ml-3 text-gray-600">{{ $t('pv.generazione.loading_trips') }}</span>
        </div>

        <!-- Error -->
        <div v-if="errorMessage" class="bg-red-50 border border-red-200 p-4 m-4 rounded">
          <p class="text-red-600">{{ errorMessage }}</p>
        </div>

        <!-- Trips Table -->
        <div v-if="trips.length > 0" class="overflow-x-auto">
          <table class="min-w-full border-collapse">
            <thead class="bg-blue-600 text-white">
              <tr>
                <th class="border border-blue-700 px-2 py-2 text-xs font-semibold text-center w-10">{{ $t('pv.generazione.th_n') }}</th>
                <th class="border border-blue-700 px-2 py-2 text-xs font-semibold text-left min-w-[140px]">{{ $t('pv.generazione.th_company_name') }}</th>
                <th class="border border-blue-700 px-2 py-2 text-xs font-semibold text-left min-w-[180px]">{{ $t('pv.generazione.th_shipping_address') }}</th>
                <th class="border border-blue-700 px-2 py-2 text-xs font-semibold text-center w-20">{{ $t('pv.generazione.th_doc_num') }}</th>
                <th class="border border-blue-700 px-2 py-2 text-xs font-semibold text-left min-w-[160px]">{{ $t('pv.generazione.th_warnings') }}</th>
                <th class="border border-blue-700 px-2 py-2 text-xs font-semibold text-center w-12">{{ $t('pv.generazione.th_nc') }}</th>
                <th class="border border-blue-700 px-2 py-2 text-xs font-semibold text-center w-16">{{ $t('pv.generazione.th_rimes') }}</th>
                <th class="border border-blue-700 px-2 py-2 text-xs font-semibold text-center w-16">{{ $t('pv.generazione.th_mdp') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="selectedOrigin" class="bg-green-50">
                <td class="border border-green-300 px-2 py-2 text-center text-base">🚩</td>
                <td class="border border-green-300 px-2 py-2 text-xs font-bold text-green-700">
                  {{ $t('pv.generazione.row_partenza_prefix') }} {{ selectedOrigin.label }}
                </td>
                <td class="border border-green-300 px-2 py-2 text-xs text-green-700">{{ selectedOrigin.address }}</td>
                <td class="border border-green-300 px-2 py-2 text-center text-xs">-</td>
                <td class="border border-green-300 px-2 py-2 text-xs">-</td>
                <td class="border border-green-300 px-2 py-2 text-center text-xs">-</td>
                <td class="border border-green-300 px-2 py-2 text-center text-xs"></td>
                <td class="border border-green-300 px-2 py-2 text-center text-xs">-</td>
              </tr>
              <tr v-for="group in mergedGroups" :key="group.key" class="hover:bg-gray-50 even:bg-gray-50">
                <td class="border border-gray-300 px-2 py-2 text-sm text-center font-bold align-middle bg-blue-50 text-blue-700">
                  {{ group.number }}
                </td>
                <td class="border px-2 py-2">
                  <template v-if="group.trips[0]?.isManual">
                    <span class="text-xs bg-yellow-200 px-1 rounded text-yellow-800 mr-1">{{ $t('pv.generazione.tag_manual') }}</span>
                    {{ group.companyName }}
                  </template>
                  <template v-else>
                    <div
                      v-for="(doc, docIndex) in group.documents_data"
                      :key="`name-${doc.id ?? 'doc'}-${doc.tipo_doc ?? ''}-${doc.num ?? ''}-${docIndex}`"
                      class="min-h-[2.5rem] flex items-center py-1 border-b border-gray-100 last:border-b-0"
                    >
                      {{ doc.companyName }}
                    </div>
                  </template>
                </td>
                <td class="border border-gray-300 px-2 py-2 text-xs">{{ group.address }}</td>
                <td class="border border-gray-300 px-2 py-2 text-sm text-center">
                  <div
                    v-for="(doc, docIndex) in group.documents_data"
                    :key="`${doc.id ?? 'doc'}-${doc.tipo_doc ?? ''}-${doc.num ?? ''}-${docIndex}`"
                    class="min-h-[2.5rem] flex flex-col justify-center py-1 border-b border-gray-100 last:border-b-0"
                  >
                    <span v-if="!group.trips[0]?.isManual" class="text-xs text-gray-500">{{ getDocType(doc.tipo_doc) }}</span>
                    <span>{{ group.trips[0]?.isManual ? '' : doc.num }}</span>
                  </div>
                </td>
                <td class="border border-gray-300 px-2 py-2 text-xs">{{ [group.telephon, group.avvertimenti].filter(Boolean).join(' | ') || '-' }}</td>
                <td class="border border-gray-300 px-2 py-2 text-sm text-center">{{ group.colli || '-' }}</td>
                <td class="border border-gray-300 px-2 py-2 text-sm text-center font-bold"
                  :class="group.rimes ? 'text-red-600' : ''">{{ group.rimes || '' }}</td>
                <td class="border border-gray-300 px-2 py-2 text-xs text-center">{{ group.mdp || '-' }}</td>
              </tr>
              <tr v-if="selectedOrigin && selectedDestination" class="bg-red-50">
                <td class="border border-red-300 px-2 py-2 text-center text-base">🏁</td>
                <td class="border border-red-300 px-2 py-2 text-xs font-bold text-red-700">
                    {{ $t('pv.generazione.row_arrivo_prefix') }} {{ selectedDestination.label }}
                </td>

                <td class="border border-red-300 px-2 py-2 text-xs text-red-700">{{ selectedDestination.address }}</td>
                <td class="border border-red-300 px-2 py-2 text-center text-xs">-</td>
                <td class="border border-red-300 px-2 py-2 text-xs">-</td>
                <td class="border border-red-300 px-2 py-2 text-center text-xs">-</td>
                <td class="border border-red-300 px-2 py-2 text-center text-xs"></td>
                <td class="border border-red-300 px-2 py-2 text-center text-xs">-</td>
              </tr>
            </tbody>
            
          </table>
        </div>
      </div>
      <div v-if="genera_ddt && pickingRows.length > 0" class="bg-white rounded-lg shadow-lg overflow-hidden mt-4">
        <div class="pt-4 overflow-x-auto border-t border-gray-200">
          <h2 class="px-3 pb-3 text-base font-semibold text-gray-800">DDT dei picking</h2>
          <table class="min-w-full border-collapse">
            <thead class="bg-blue-600 text-white">
              <tr>
                <th class="border border-blue-700 px-3 py-2 text-left text-xs font-semibold">Picking</th>
                <th class="border border-blue-700 px-3 py-2 text-left text-xs font-semibold">Ordine</th>
                <th class="border border-blue-700 px-3 py-2 text-left text-xs font-semibold">Cliente</th>
                <th class="border border-blue-700 px-3 py-2 text-center text-xs font-semibold">Stato DDT</th>
                <th class="border border-blue-700 px-3 py-2 text-center text-xs font-semibold">Azione</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="picking in pickingRows"
                :key="String(picking.id)"
                class="even:bg-gray-50 hover:bg-gray-50"
                :class="{ 'animate-pulse bg-blue-50': isDdtGenerating(picking.id) }"
              >
                <td class="border border-gray-300 px-3 py-2 text-xs">{{ picking.id }}</td>
                <td class="border border-gray-300 px-3 py-2 text-xs">{{ picking.num || '-' }}</td>
                <td class="border border-gray-300 px-3 py-2 text-xs">{{ picking.CompanyName || picking.companyName || picking.cli || '-' }}</td>
                <td class="border border-gray-300 px-3 py-2 text-center text-xs">
                  <span v-if="Number(picking.delivered) === 1" class="rounded bg-green-100 px-2 py-1 font-semibold text-green-800">DDT presente</span>
                  <span v-else class="rounded bg-amber-100 px-2 py-1 font-semibold text-amber-800">Da generare</span>
                  <div v-if="ddtErrors[String(picking.id)]" class="mt-1 text-left text-red-700">
                    {{ ddtErrors[String(picking.id)] }}
                  </div>
                </td>
                <td class="border border-gray-300 px-3 py-2 text-center">
                  <button
                    v-if="Number(picking.delivered) !== 1"
                    @click="generateDdt(picking.id)"
                    :disabled="!lastSavedId || isDdtGenerating(picking.id)"
                    class="rounded bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {{ isDdtGenerating(picking.id) ? 'Generazione...' : 'Genera DDT' }}
                  </button>
                  <button
                    type="button"
                    @click="printDdt(picking.ddtDocNo)"
                    :disabled="Number(picking.delivered) !== 1 || !picking.ddtDocNo"
                    :class="Number(picking.delivered) === 1 && picking.ddtDocNo ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-gray-300 text-gray-600 cursor-not-allowed'"
                    class="ml-2 rounded px-3 py-1.5 text-xs font-medium disabled:cursor-not-allowed"
                  >
                    Stampa DDT
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- No Data -->
        <div v-if="hasSearched && !isLoading && trips.length === 0 && !errorMessage"
          class="text-center py-12 text-gray-500">
          <p class="text-lg">{{ $t('pv.generazione.no_trips_found') }}</p>
        </div>

        <!-- Initial State -->
        <div v-if="!hasSearched && !isLoading" class="text-center py-12 text-gray-500">
          <svg class="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path d="M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0 2 2 0 014 0z" />
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0" />
          </svg>
          
          <button @click="showSelectionModal = true"
                class="mx-auto px-6 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded text-xs font-medium flex items-center justify-center gap-1">
                <v-icon name="hi-refresh" scale="0.9" /> {{ $t('pv.generazione.select_carrier_period') }}
        </button>
        </div>
      </div>
    </div>

    <!-- Selection Modal -->
    <div v-if="showSelectionModal"
      class="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white rounded-lg shadow-xl p-6 w-full max-w-lg mx-4" @click.stop>
        <h2 class="text-xl font-semibold text-gray-900 mb-6 flex items-center">
          <v-icon name="co-truck" class="mr-2 text-blue-600" scale="1.5" />
          {{ $t('pv.generazione.modal_title') }}
        </h2>

        <!-- Carrier Selection -->
        <div class="mb-6">
          <label class="block text-sm font-medium text-gray-700 mb-2">{{ $t('pv.generazione.carrier_label') }}</label>
          <v-select v-model="selectedCarrier" :options="carriers" label="Carrier" :reduce="carrier => carrier.Carrier"
            :placeholder="$t('pv.generazione.carrier_placeholder')" class="vue-select-style">
            <template #option="{ Carrier }">
              <span class="font-medium">{{ Carrier }}</span>
            </template>
            <template #selected-option="{ Carrier }">
              {{ Carrier }}
            </template>
          </v-select>
        </div>

        <!-- Date Mode Selection -->
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-2">{{ $t('pv.generazione.search_mode') }}</label>
          <div class="flex p-1 bg-gray-100 rounded-lg">
            <button 
              @click="dateMode = 'single'"
              type="button"
              class="flex-1 py-1.5 text-xs font-medium rounded-md transition-all"
              :class="dateMode === 'single' ? 'bg-white shadow-sm text-blue-600' : 'text-gray-500 hover:text-gray-700'"
            >
              {{ $t('pv.generazione.single_date') }}
            </button>
            <button 
              @click="dateMode = 'range'"
              type="button"
              class="flex-1 py-1.5 text-xs font-medium rounded-md transition-all"
              :class="dateMode === 'range' ? 'bg-white shadow-sm text-blue-600' : 'text-gray-500 hover:text-gray-700'"
            >
              {{ $t('pv.generazione.date_range') }}
            </button>
          </div>
        </div>

        <!-- Date Selection -->
        <div class="mb-6">
          <label class="block text-sm font-medium text-gray-700 mb-2">
            {{ dateMode === 'single' ? $t('pv.generazione.select_date') : $t('pv.generazione.select_dates') }}
          </label>
          <div :class="dateMode === 'range' ? 'grid grid-cols-2 gap-4' : ''">
            <div>
              <label v-if="dateMode === 'range'" class="block text-xs text-gray-500 mb-1">{{ $t('pv.generazione.date_from') }}</label>
              <VueDatePicker v-model="dateFrom" locale="it" :enable-time-picker="false" format="dd/MM/yyyy" auto-apply text-input class="w-full" @input="pageDateInput">
              </VueDatePicker>
            </div>
            <div v-if="dateMode === 'range'">
              <label class="block text-xs text-gray-500 mb-1">{{ $t('pv.generazione.date_to') }}</label>
              <VueDatePicker v-model="dateTo" locale="it" :enable-time-picker="false" format="dd/MM/yyyy" auto-apply text-input class="w-full" @input="pageDateInput">
              </VueDatePicker>
            </div>
          </div>
        </div>


        <!-- Action Buttons -->
        <div class="flex justify-end gap-3 pt-4 border-t border-gray-200">
          <button @click="closeModal"
            class="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors">
            {{ $t('actions.cancel') }}
          </button>
          <button @click="fetchTrips" :disabled="!isFormValid || isLoading"
            class="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center">
            <v-icon name="co-search" class="mr-1" />
            {{ $t('pv.generazione.btn_search_trips') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Route Editor Modal -->
    <div v-if="showRouteEditor" class="fixed left-0 right-0 bottom-0 z-50" style="top: 80px;">
      <div class="bg-white w-full h-full overflow-hidden flex flex-col">
        <!-- Header -->
        <div class="bg-gradient-to-r from-green-600 to-green-700 text-white py-2 px-4 flex justify-between items-center">
          <h2 class="text-xl font-bold flex items-center">
            <v-icon name="co-map" class="mr-2" /> {{ $t('pv.generazione.route_editor_title') }}
          </h2>
          <button @click="closeRouteEditor" :disabled="isLoading" class="text-white hover:text-gray-200 disabled:opacity-50">
            <v-icon name="co-x" scale="1.5" />
          </button>
        </div>

        <!-- Content -->
        <div class="flex flex-col lg:flex-row overflow-hidden min-h-0 flex-1">
          <!-- Left Panel - Waypoints List -->
          <div class="w-full lg:w-1/4 border-b lg:border-b-0 lg:border-r border-gray-200 p-3 lg:p-4 bg-gray-50 overflow-y-auto max-h-[30vh] lg:max-h-none">
            <div class="flex items-center justify-between mb-3">
              <h3 class="text-sm font-semibold text-gray-700">{{ $t('pv.generazione.stops_count') }} ({{ waypoints.length }})</h3>
              <button
                @click="reverseRoute"
                :disabled="waypoints.length < 2 || isLoading || isCalculatingRoute"
                class="px-2 py-1 text-xs font-medium text-blue-700 bg-white border border-blue-300 rounded hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
              >
                <v-icon name="hi-switch-horizontal" scale="0.8" /> Inverti percorso
              </button>
            </div>
            <div class="mb-4 bg-white p-3 rounded-lg shadow-sm border border-gray-200">
              <label class="block text-xs font-bold text-gray-700 mb-1">{{ $t('pv.shared.add_waypoint') }}</label>
                <div class="flex gap-2">
                  <input 
                    ref="newWaypointInput"
                    type="text" 
                    :placeholder="$t('pv.shared.search_address_placeholder')"
                    class="flex-1 text-sm border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                    :disabled="isLoading"
                  />
                  <button 
                    @click="addNewWaypoint"
                    class="bg-blue-600 text-white px-3 rounded hover:bg-blue-700 disabled:opacity-50"
                    :disabled="!tempPlace || isLoading"
                  >
                    <v-icon name="co-plus" scale="0.8" />
                  </button>
                </div>
            </div>
            <draggable v-model="waypoints" item-key="id" @end="onDragEnd" class="space-y-2" handle=".drag-handle" :disabled="isLoading" :animation="200" ghost-class="drag-ghost" chosen-class="drag-chosen">
              <template #item="{ element: wp, index }">
                <div class="bg-white rounded-lg p-1 shadow-sm border-l-4 relative group"
                  :class="{ 'border-green-500': index === 0, 'border-red-500': index === waypoints.length - 1, 'border-blue-500': index > 0 && index < waypoints.length - 1 }">
                  <button 
                    @click="removeWaypoint(index)"
                    class="absolute top-2 right-2 text-gray-400 hover:text-red-600 transition-colors disabled:opacity-50"
                    :title="$t('pv.shared.delete_waypoint_title')"
                    :disabled="isLoading"
                  >
                    <v-icon name="co-x" scale="0.8" />
                  </button> 
                  
                  
                  <div class="flex items-center gap-2">
                    <div class="drag-handle text-gray-400 hover:text-gray-600 shrink-0" :class="isLoading ? 'cursor-not-allowed' : 'cursor-move'">≡</div>
                    <span class="shrink-0 font-bold text-xs">
                      {{ index === 0 ? '🚩' : index === waypoints.length - 1 ? '🏁' : index }}
                    </span>
                    <div class="min-w-0 flex-1">
                      <div class="text-[10px] text-gray-600 truncate" :title="((wp.allCli && wp.allCli.length ? (usa_categoria_cliente && wp.Category === usa_categoria_cliente ? wp.allCliSede : wp.allCli).join(', ') + ' · ' : '') + wp.address)">
                        <span v-if="wp.allCli && wp.allCli.length" class="font-bold text-blue-600">{{ (usa_categoria_cliente && wp.Category === usa_categoria_cliente ? wp.allCliSede : wp.allCli).join(', ') }} · </span>{{ wp.address }}
                      </div>
                      <div v-if="wp.telephon || wp.avvertimenti" class="text-gray-400 text-[10px] truncate" :title="[wp.telephon, wp.avvertimenti].filter(Boolean).join(' | ')">
                        {{ [wp.telephon, wp.avvertimenti].filter(Boolean).join(' | ') }}
                      </div>
                    </div>
                  </div>
                  
                </div>
              </template>
            </draggable>
          </div>

          <!-- Center - Map -->
          <div class="flex-1 relative min-h-[40vh] lg:min-h-0">
            
            <div v-if="isCalculatingRoute || isLoading"
              class="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10">
              <div class="text-center">
                <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto"></div>
                <p class="mt-2 text-gray-600 font-bold uppercase text-xs">{{ isLoading ? $t('pv.generazione.saving_in_progress') : $t('pv.generazione.route_calculating') }}</p>
              </div>
            </div>
            <button @click="showOptionsPanel = !showOptionsPanel" 
              class="absolute top-2 mt-5 right-6 z-20 bg-white p-3 rounded-lg shadow-md hover:bg-gray-100 border rotate-90">
              <v-icon name="hi-switch-horizontal" class="rotate-90" /> &nbsp; {{ $t('pv.shared.options') }}
            </button>
            
            <RouteEditorMapHere
              v-if="useHereMap"
              ref="routeMap"
              :api-key="routing_provider_key"
              :strategy="routingStrategy"
              :vehicle="selectedVehicleData"
              :waypoints="waypoints"
              :center="mapCenter"
              :zoom="10"
              :avoid-tolls="avoidTolls"
              :avoid-highways="avoidHighways"
              :avoid-ferries="avoidFerries"
              :optimize-route="optimizeRoute"
              @ready="onRouteMapReady"
              @route-calculated="onRouteCalculated"
              @route-error="onRouteError"
              @waypoints-optimized="onWaypointsOptimized"
              @adjacent-duplicates-detected="onAdjacentDuplicatesDetected"
              style="width: 100%; height: 100%;"
            />
            
            <RouteEditorMapGoogle
              v-else
              ref="routeMap"
              :api-key="routing_provider_key"
              :strategy="routingStrategy"
              :waypoints="waypoints"
              :center="mapCenter"
              :zoom="10"
              :avoid-tolls="avoidTolls"
              :avoid-highways="avoidHighways"
              :avoid-ferries="avoidFerries"
              :optimize-route="optimizeRoute"
              @ready="onRouteMapReady"
              @route-calculated="onRouteCalculated"
              @route-error="onRouteError"
              @waypoints-optimized="onWaypointsOptimized"
            />
          </div>

          <!-- Right Panel - Controls -->
          <div v-show="showOptionsPanel" class="relative right-0 top-0 bottom-0 w-full lg:w-1/4 p-2 lg:p-4 bg-gray-50 border-t lg:border-t-0 lg:border-l border-gray-200 overflow-y-auto z-30 shadow-xl max-h-[40vh] lg:max-h-none">
            <h3 class="text-sm font-semibold mb-3 text-gray-700">{{ $t('pv.shared.options') }}</h3>

            <!-- Avoid Tolls Toggle -->
            <div class="mb-4 bg-white rounded-lg p-3 shadow-sm">
              <label class="flex items-center cursor-pointer">
                <input type="checkbox" v-model="avoidTolls" @change="calculateRoute" :disabled="isLoading || isCalculatingRoute"
                  class="h-4 w-4 text-green-600 border-gray-300 rounded mr-3" />
                <div>
                  <div class="font-semibold text-sm text-gray-800">{{ $t('pv.shared.avoid_tolls') }}</div>
                  <div class="text-xs text-gray-500">{{ $t('pv.generazione.option_avoid_tolls_desc') }}</div>
                </div>
              </label>
            </div>
            <div class="mb-4 bg-white rounded-lg p-3 shadow-sm">
              <label class="flex items-center cursor-pointer">
                <input type="checkbox" v-model="avoidHighways" @change="calculateRoute" :disabled="isLoading || isCalculatingRoute"
                  class="h-4 w-4 text-green-600 border-gray-300 rounded mr-3" />
                <div>
                  <div class="font-semibold text-sm text-gray-800">{{ $t('pv.shared.avoid_highways') }}</div>
                  <div class="text-xs text-gray-500">{{ $t('pv.generazione.option_avoid_highways_desc') }}</div>
                </div>
              </label>
            </div>

            <div class="mb-4 bg-white rounded-lg p-3 shadow-sm">
              <label class="flex items-center cursor-pointer">
                <input type="checkbox" v-model="avoidFerries" @change="calculateRoute" :disabled="isLoading || isCalculatingRoute"
                  class="h-4 w-4 text-green-600 border-gray-300 rounded mr-3" />
                <div>
                  <div class="font-semibold text-sm text-gray-800">{{ $t('pv.shared.avoid_ferries') }}</div>
                  <div class="text-xs text-gray-500">{{ $t('pv.generazione.option_avoid_ferries_desc') }}</div>
                </div>
              </label>
            </div>

            <!-- Optimize Route Button -->
            <button @click="calculateRoute(false, true)" :disabled="isLoading || isCalculatingRoute || useHereMap"
              class="w-full bg-white border border-green-600 text-green-600 px-4 py-2 rounded-lg hover:bg-green-50 disabled:bg-gray-100 disabled:border-gray-300 disabled:text-gray-400 disabled:cursor-not-allowed text-sm font-bold mb-2 flex items-center justify-center gap-2">
              <v-icon name="hi-lightning-bolt" scale="0.8" /> {{ $t('pv.generazione.btn_optimize_route') }}
            </button>

            <!-- Recalculate Button -->
            <button @click="calculateRoute" :disabled="isCalculatingRoute || isLoading"
              class="w-full bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 disabled:bg-gray-400 text-sm font-semibold mb-4">
              {{ $t('pv.generazione.btn_calculate_route') }}
            </button>

            <!-- Route Info -->
            <div v-if="routeData" class="bg-white rounded-lg p-3 shadow-sm">
              <h4 class="font-semibold text-sm mb-2 text-gray-800">{{ $t('pv.generazione.route_info_title') }}</h4>
              <div class="space-y-2 text-sm">
                <div class="flex justify-between">
                  <span class="text-gray-600">{{ $t('pv.generazione.distance_label') }}</span>
                  <span class="font-bold text-green-600">{{ routeData.distance }}</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-gray-600">{{ $t('pv.generazione.duration_label') }}</span>
                  <span class="font-bold text-green-600">{{ routeData.duration }}</span>
                </div>
              </div>
            </div>

            <!-- Route Error -->
            <div v-if="routeError" class="mt-4 bg-red-50 border border-red-200 rounded-lg p-3">
              <p class="text-xs text-red-600">{{ routeError }}</p>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="border-t border-gray-200 py-2 px-4 bg-gray-50 flex justify-end gap-3">
          <button @click="closeRouteEditor" :disabled="isLoading" class="px-4 py-2 text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-lg disabled:opacity-50">
            {{ $t('actions.cancel') }}
          </button>
          <button @click="applyRouteOrder" :disabled="isLoading || isCalculatingRoute"
            class="px-8 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-bold shadow-md transition-all disabled:opacity-50 flex items-center justify-center gap-2 min-w-[160px]">
            <div v-if="isLoading" class="animate-spin h-3 w-3 border-2 border-white border-t-transparent rounded-full"></div>
            <span>{{ isLoading ? $t('pv.shared.saving') : $t('pv.generazione.btn_save_route') }}</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Duplicates Modal -->
    <div v-if="showDuplicatesModal" class="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
        <div class="flex items-center gap-3 mb-4">
          <div class="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
            <svg class="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4v2m0 0v2m0 0V3m0 0l4 0m0 0l0 4m0 0l-4 0m0 0l0-4" />
            </svg>
          </div>
          <h2 class="text-lg font-bold text-gray-900">Tappe consecutive con lo stesso indirizzo</h2>
        </div>

        <p class="text-sm text-gray-600 mb-4">
          Sono state rilevate {{ pendingDuplicates?.length || 0 }} tappe consecutive con lo stesso indirizzo. 
          Here Maps non supporta questo scenario.
        </p>

        <div class="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
          <ul class="text-xs text-gray-700 space-y-1">
            <li v-for="(group, idx) in pendingDuplicates" :key="idx" class="flex items-start">
              <span class="font-bold mr-2">{{ group.start }}-{{ group.end }}:</span>
              <span class="flex-1">{{ waypoints.slice(group.start, group.end + 1).map(w => w.companyName).join(', ') }}</span>
            </li>
          </ul>
        </div>

        <div class="flex justify-end gap-3">
          <button @click="closeDuplicatesModal"
            class="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors">
            Riorganizza manualmente
          </button>
          <button @click="mergeDuplicateWaypoints"
            class="px-6 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 font-bold transition-colors">
            Accorpa automaticamente
          </button>
        </div>
      </div>
    </div>
  </div>

  
  </div>
</template>

<script>
import { Link } from '@inertiajs/vue3';
import BreadCrumbs from '@/Components/BreadCrumbs.vue';
import draggable from 'vuedraggable';
import axios from 'axios';
import QrcodeVue from 'qrcode.vue';
import RouteEditorMapGoogle from '@/Pages/Modules/percorsi-vettori/RouteEditorMapGoogle.vue';
import RouteEditorMapHere from '@/Pages/Modules/percorsi-vettori/RouteEditorMapHere.vue';
import { createRoutingStrategy } from '@/Pages/Modules/percorsi-vettori/routingStrategies/routingFactory';

export default {
  components: {
    Link,
    BreadCrumbs,
    draggable,
    QrcodeVue,
    RouteEditorMapGoogle,
    RouteEditorMapHere,
  },

  props: {
    carriers: {
      type: Array,
      default: () => []
    },
    companyInfo: {
      type: Array,
      default: () => []
    },
    companyBranches: {
      type: Array,
      default: () => []
    },
    group_by_companyName_and_address: {
      type: Boolean,
      default: false
    },
    usa_categoria_cliente: {
      type: String,
      default: ''
    },
    preloadedTrips: {
      type: Array,
      default: () => []
    },
    routing_provider: {
      type: String,
      default: 'google'
    },
    routing_provider_key: {
      type: String,
      default: ''
    },
    genera_ddt: {
      type: Boolean,
      default: false
    },
    specifiche_veicoli: {
      type: Boolean,
      default: false
    },
    veicoli: {
      type: Array,
      default: () => []
    }
  },

  data() {
    return {
      navigation: [
        { name: 'Dashboard', href: route('dashboard') },
        { name: this.$t('pv.breadcrumb.percorsi_vettori'), href: route('modules.home', 'percorsi-vettori') },
        { name: this.$t('pv.breadcrumb.generazione_itinerari'), href: '#' }
      ],
      // Selection modal
      showSelectionModal: true,
      showOptionsPanel: false,
      selectedCarrier: null,
      selectedVehicle: '',
      carrierInfo: null,
      dateFrom: new Date(),
      dateTo: new Date(),
      dateMode: 'single',
      trips: [],
      isLoading: false,
      errorMessage: '',
      hasSearched: false,
      // Route editor
      showRouteEditor: false,
      mergeSameAddress: false,
      waypoints: [],
      mapCenter: { lat: 45.0732, lng: 7.6827 },
      isCalculatingRoute: false,
      optimizeRoute: false,
      avoidTolls: false,
      avoidHighways: false, 
      avoidFerries: false,
      routeData: null,
      routeError: '',
      selectedOrigin: null,
      tempPlace: null,
      generatedMapsLink: null,
      travelName: '',
      lastSavedId: null,
      _routePayload: null,
      customOrigins: [],  
      showCustomOriginInput: false, 
      customOriginAutocomplete: null, 
      selectedDestination: null,
      customDestinations: [],
      showCustomDestinationInput: false,
      customDestinationAutocomplete: null,
      originPredictions: [],
      destinationPredictions: [],
      routingStrategy: null,
      showDuplicatesModal: false,
      pendingDuplicates: null,
      generatingDdtIds: [],
      ddtErrors: {},
    };
  },

  computed: {
    normalizedRoutingProvider() {
      const provider = String(this.routing_provider || '').toLowerCase().trim();
      return ['google', 'here'].includes(provider) ? provider : 'google';
    },

    useHereMap() {
      const result = this.normalizedRoutingProvider === 'here';
      return result;
    },

    useGoogleMap() {
      return this.normalizedRoutingProvider === 'google';
    },

    selectedVehicleData() {
      return this.veicoli.find(vehicle => String(vehicle.id) === String(this.selectedVehicle)) || null;
    },

    hasPreloadedTrips() {
      return Array.isArray(this.preloadedTrips) && this.preloadedTrips.length > 0;
    },
    pickingRows() {
      const rowsById = new Map();
      this.trips.forEach(trip => {
        if (trip.isManual || trip.id === null || trip.id === undefined || String(trip.id).trim() === '') return;
        const id = String(trip.id);
        const existing = rowsById.get(id);
        if (!existing || Number(existing.delivered) !== 1 && Number(trip.delivered) === 1) {
          rowsById.set(id, trip);
        }
      });
      return Array.from(rowsById.values());
    },
    pickingIds() {
      return this.pickingRows.map(picking => String(picking.id));
    },
    isFormValid() {
      const carrierOk = !!this.selectedCarrier;
      const datesOk = this.dateMode === 'single' 
        ? !!this.dateFrom 
        : (!!this.dateFrom && !!this.dateTo);
      return carrierOk && datesOk;
    },
    totalColli() {
      return this.trips.reduce((sum, trip) => sum + (parseInt(trip.colli) || 0), 0);
    },
    originOptions() {
        let options = [];
        // Info da Sede Centrale
        if (this.companyInfo && this.companyInfo.length > 0) {
            const main = this.companyInfo[0];
            options.push({
                label: `${this.$t('pv.generazione.label_sede_legale')} - ${main.CompanyName}`,
                address: `${main.Address}, ${main.City}`,
                lat: main.Latitude,
                lng: main.Longitude
            });
        }
        // Filiali da companyBranches
        if (this.companyBranches) {
            this.companyBranches.forEach(branch => {
                options.push({
                    label: `${this.$t('pv.generazione.label_deposito')} - ${branch.Description}`,
                    address: `${branch.Address}, ${branch.City}`,
                    lat: branch.Latitude,
                    lng: branch.Longitude
                });
            });
        }
        
        this.customOrigins.forEach(c => options.push(c));
        return options;
    },
    destinationOptions() {
        let options = [];
        if (this.companyInfo && this.companyInfo.length > 0) {
            const main = this.companyInfo[0];
            options.push({
                label: `${this.$t('pv.generazione.label_sede_legale')} - ${main.CompanyName}`,
                address: `${main.Address}, ${main.City}`,
                lat: main.Latitude,
                lng: main.Longitude
            });
        }
        if (this.companyBranches) {
            this.companyBranches.forEach(branch => {
                options.push({
                    label: `${this.$t('pv.generazione.label_deposito')} - ${branch.Description}`,
                    address: `${branch.Address}, ${branch.City}`,
                    lat: branch.Latitude,
                    lng: branch.Longitude
                });
            });
        }
        this.customDestinations.forEach(c => options.push(c));
        return options;
    },

    mergedGroups() {
      const getKey = (t) => {
        const delivery = this.resolveDeliveryData(t);
        return this.group_by_companyName_and_address
          ? `${delivery.address?.trim().toLowerCase()}|||${(delivery.companyName || t.cli || '').trim().toLowerCase()}`
          : delivery.address?.trim().toLowerCase();
      };

      if (!this.mergeSameAddress) {
        return this.trips.map((t, i) => {
          const delivery = this.resolveDeliveryData(t);
          return {
            key: `trip-${t.id ?? `${t.tipo_doc}-${t.num}`}-${i}`,
            number: i + 1,
            span: 1,
            trips: [t],
            id: t.id,
            allIds: [t.id],
            allCli: [t.cli].filter(Boolean),
            allCliSede: [t.cli && t.sede ? `${t.cli}/${t.sede}` : t.cli].filter(Boolean),
            Category: t.Category || '',
            address: delivery.address,
            companyName: delivery.companyName,
            cli: t.cli,
            lat: delivery.lat,
            lng: delivery.lng,
            colli: parseInt(t.colli) || 0,
            rimes: t.rimes || '',
            mdp: t.mdp || '',
            notes: t.note || '',
            telephon: delivery.telephon,
            avvertimenti: t.avvertimenti || '',
            documents_data: [{
              id: t.id, num: t.num, tipo_doc: t.tipo_doc,
              cli: t.cli, sede: t.sede, colli: t.colli,
              rimes: t.rimes, mdp: t.mdp, note: t.note,
              telephon: delivery.telephon,
              avvertimenti: t.avvertimenti,
              companyName: delivery.companyName,
            }],
          };
        });
      }

      const map = new Map();
      const order = [];

      this.trips.forEach(t => {
        const key = getKey(t);
        const delivery = this.resolveDeliveryData(t);
        if (!map.has(key)) {
          order.push(key);
          map.set(key, {
            key,
            trips: [],
            id: t.id,
            allIds: [],
            allCli: [],
            allCliSede: [],
            Category: t.Category || '',
            address: delivery.address,
            companyName: delivery.companyName,
            cli: t.cli,
            lat: delivery.lat,
            lng: delivery.lng,
            colli: 0,
            rimes: '',
            mdp: '',
            notes: '',
            telephon: '',
            avvertimenti: '',
            documents_data: [],
          });
        }

        const group = map.get(key);
        group.trips.push(t);
        group.allIds.push(t.id);

        if (t.cli && !group.allCli.includes(t.cli)) group.allCli.push(t.cli);
        const cliSedeEntry = t.cli && t.sede ? `${t.cli}/${t.sede}` : t.cli;
        if (cliSedeEntry && !group.allCliSede.includes(cliSedeEntry)) group.allCliSede.push(cliSedeEntry);

        group.documents_data.push({
          id: t.id, num: t.num, tipo_doc: t.tipo_doc,
          cli: t.cli, sede: t.sede, colli: t.colli,
          rimes: t.rimes, mdp: t.mdp, note: t.note,
          telephon: delivery.telephon,
          avvertimenti: t.avvertimenti,
          companyName: delivery.companyName,
        });

        group.colli += parseInt(t.colli) || 0;
        if (t.rimes && !group.rimes.includes(t.rimes))
          group.rimes += group.rimes ? ` | ${t.rimes}` : t.rimes;
        if (t.mdp && !group.mdp.includes(t.mdp))
          group.mdp += group.mdp ? ` | ${t.mdp}` : t.mdp;
        if (t.note && !group.notes.includes(t.note))
          group.notes += group.notes ? ` | ${t.note}` : t.note;
        if (delivery.telephon && !group.telephon.includes(delivery.telephon))
          group.telephon += group.telephon ? ` | ${delivery.telephon}` : delivery.telephon;
        if (t.avvertimenti && !group.avvertimenti.includes(t.avvertimenti))
          group.avvertimenti += group.avvertimenti ? ` | ${t.avvertimenti}` : t.avvertimenti;
      });

      return order.map((key, i) => ({
        ...map.get(key),
        number: i + 1,
        span: map.get(key).trips.length,
      }));
    },

  },

  methods: {
    closeModal() {
      this.showSelectionModal = false;
    },

    getCarrierAddressInfo(carrierCode) {
      if (!carrierCode) return null;
      const carrier = (this.carriers || []).find(c => c.Carrier === carrierCode);
      if (!carrier) {
        console.warn('[GenerazioneItinerari] Vettore2 non trovato tra i vettori disponibili:', carrierCode);
        return null;
      }
      return {
        companyName: carrier.CompanyName || carrierCode,
        address: [carrier.Address, carrier.City].filter(Boolean).join(', '),
        telephon: carrier.Telephone1 || '',
      };
    },

    resolveDeliveryData(t) {
      if (t.vettore2) {
        const carrierInfo = this.getCarrierAddressInfo(t.vettore2);
        if (carrierInfo) {
          return {
            companyName: carrierInfo.companyName,
            address: carrierInfo.address,
            telephon: carrierInfo.telephon,
            lat: null,
            lng: null,
          };
        }
      }
      return {
        companyName: t.CompanyName || t.companyName || t.cli,
        address: t.shippingAddress,
        telephon: t.telephon || '',
        lat: t.latitude,
        lng: t.longitude,
      };
    },


    async fetchTrips() {
      
      if (!this.isFormValid) return;

      this.isLoading = true;
      this.errorMessage = '';
      this.lastSavedId = null;
      this.carrierInfo = this.carriers.find(c => c.Carrier === this.selectedCarrier);

      try {
        const fromStr = this.formatDate(this.dateFrom);
        // Se è data singola, "to" è uguale a "from"
        const toStr = this.dateMode === 'single' ? fromStr : this.formatDate(this.dateTo);
        const response = await axios.post(route('modules.percorsi-vettori.trips'), {
          carrier: this.selectedCarrier,
          from: fromStr,
          to: toStr
        });
        
        this.trips = response.data || [];
        this.hasSearched = true;
        this.showSelectionModal = false;
      } catch (error) {
        console.error('Error fetching trips:', error);
        this.errorMessage = error.response?.data?.error || this.$t('pv.generazione.fetch_error_default');
      } finally {
        this.isLoading = false;
      }
    },

    onRouteMapReady() {
      this.calculateRoute();
    },

    onRouteCalculated(payload) {
      this.isCalculatingRoute = false;
      this.routeError = '';

      this._routePayload = payload;

      this.routeData = {
        distance: payload.distanceText,
        duration: payload.durationText
      };

      this.optimizeRoute = false;
      this.syncTripsOrder();
    },

    onRouteError(error) {
      console.error('[onRouteError] Route error:', error);

      this.isCalculatingRoute = false;
      this.routeError = error?.message || this.$t('pv.generazione.route_error_default') || 'Errore durante il calcolo del percorso';
    },

    onWaypointsOptimized(optimizedWaypoints) {
      if (!Array.isArray(optimizedWaypoints) || optimizedWaypoints.length < 2) return;

      this.waypoints = optimizedWaypoints;
      this.optimizeRoute = false;
      this.syncTripsOrder();
    },

    // Route Editor Methods
    openRouteEditor() {

      const tripWaypoints = this.mergedGroups.map(g => {
        const lat = parseFloat(g.lat);
        const lng = parseFloat(g.lng);
        return {
          id: g.id,
          allIds: g.allIds,
          allCli: g.allCli,
          allCliSede: g.allCliSede,
          Category: g.Category,
          documents_data: g.documents_data,
          address: g.address,
          companyName: g.companyName,
          cli: g.cli,
          lat: isNaN(lat) ? null : lat,
          lng: isNaN(lng) ? null : lng,
          colli: g.colli,
          rimes: g.rimes,
          mdp: g.mdp,
          notes: g.notes,
          avvertimenti: g.avvertimenti,
          needsGeocoding: isNaN(lat) || isNaN(lng),
        };
      });

      // Origine selezionata come primo waypoint
      if (this.selectedOrigin && this.selectedDestination) {
          
          // Imposta il centro mappa sulle coordinate del punto di partenza
          const originLat = parseFloat(this.selectedOrigin.lat);
          const originLng = parseFloat(this.selectedOrigin.lng);
          
          if (!isNaN(originLat) && !isNaN(originLng)) {
              this.mapCenter = { lat: originLat, lng: originLng };
          } else if (this.selectedOrigin.address) {
              // Se mancano le coordinate, usa il geocoder come richiesto
              this.geocodeAndSetCenter(this.selectedOrigin.address);
          }

          this.waypoints = [
              { 
                  id: 'origin', 
                  address: this.selectedOrigin.address, 
                  companyName: 'PARTENZA: ' + this.selectedOrigin.label,
                  lat: originLat,
                  lng: originLng,
                  needsGeocoding: isNaN(originLat) || isNaN(originLng)
              },
              ...tripWaypoints,
              { 
                id: 'destination',
                address: this.selectedDestination.address,
                companyName: 'ARRIVO: ' + this.selectedDestination.label,
                lat: parseFloat(this.selectedDestination.lat),
                lng: parseFloat(this.selectedDestination.lng),
                needsGeocoding: isNaN(parseFloat(this.selectedDestination.lat)) || isNaN(parseFloat(this.selectedDestination.lng))
              }
          ];
      } else {
          this.waypoints = tripWaypoints;
      }

      this.showRouteEditor = true;

      this.$nextTick(() => {
       
        this.geocodeWaypoints().then(() => {
          
          this.calculateRoute();
        });
      });
    },

    closeRouteEditor() {
      this.syncTripsOrder();
      this.showRouteEditor = false;
    },

    openCustomOriginInput() {
      this.showCustomOriginInput = true;
    },


    preloadApi() {
      if (this.routingStrategy) {
        this.routingStrategy.preloadApi();
      }
    },


    getStopNumber(index) {
      if (!this.mergeSameAddress) return index + 1;
      
      let stopCount = 0;
      const seen = new Set();
      
      for (let i = 0; i <= index; i++) {
        const t = this.trips[i];
        const key = this.group_by_companyName_and_address
          ? `${t.shippingAddress?.trim().toLowerCase()}_${(t.CompanyName || t.companyName || t.cli || '').trim().toLowerCase()}`
          : t.shippingAddress?.trim().toLowerCase();
        if (!seen.has(key)) {
          seen.add(key);
          stopCount++;
        }
      }
      return stopCount;
    },
    getGroupMetadata(index) {
        const currentTrip = this.trips[index];
        // Se l'accorpamento è disattivato, comportamento standard
        if (!this.mergeSameAddress) {
          return { isFirst: true, span: 1, number: index + 1 };
        }

        const getKey = (t) => this.group_by_companyName_and_address
          ? `${t.shippingAddress?.trim().toLowerCase()}_${(t.CompanyName || t.companyName || t.cli || '').trim().toLowerCase()}`
          : t.shippingAddress?.trim().toLowerCase();
        const currentKey = getKey(currentTrip);

        // Se non è la prima riga del gruppo, non renderizzare la cella
        if (index > 0 && getKey(this.trips[index - 1]) === currentKey) {
          return { isFirst: false, span: 0, number: 0 };
        }

        // Calcola quante righe consecutive hanno lo stesso indirizzo (span)
        let span = 1;
        for (let i = index + 1; i < this.trips.length; i++) {
          if (getKey(this.trips[i]) === currentKey) span++;
          else break;
        }

        // Calcola il numero della tappa progressivo (quanti gruppi unici ci sono fino a qui)
        let stopNumber = 0;
        const seen = new Set();
        for (let i = 0; i <= index; i++) {
          const key = getKey(this.trips[i]);
          if (!seen.has(key)) {
            seen.add(key);
            stopNumber++;
          }
        }

        return { isFirst: true, span, number: stopNumber };
      },

    removeWaypoint(index) {
      const wp = this.waypoints[index];
      if (wp.id === 'origin' || wp.id === 'destination') return;

      const ids = wp.allIds || [wp.id];
      if (ids.length > 1) {
        if (!confirm(this.$t('pv.generazione.confirm_remove_grouped_docs', { count: ids.length, name: wp.companyName }))) return;
      }

      this.waypoints.splice(index, 1);
      this.trips = this.trips.filter(t => !ids.includes(t.id));
      this.lastSavedId = null;

      if (this.waypoints.length >= 2) {
        this.refreshRoute();
      } else {
        this.clearRoute();
      }
    },

    handleDateInput(event, onInput) {
      let val = event.target.value.replace(/\D/g, ''); // Rimuove caratteri non numerici
      if (val.length > 8) val = val.substring(0, 8); // Limita a 8 cifre
      
      let formatted = '';
      if (val.length > 0) {
        formatted = val.substring(0, 2);
        if (val.length > 2) {
          formatted += '/' + val.substring(2, 4);
          if (val.length > 4) {
            formatted += '/' + val.substring(4, 8);
          }
        }
      }

      event.target.value = formatted; // Aggiorna visualmente l'input
      onInput(event); // Notifica VueDatePicker del nuovo valore
    },

    reverseRoute() {
      if (this.isLoading || this.isCalculatingRoute || this.waypoints.length < 2) return;

      if (this.selectedOrigin && this.selectedDestination) {
        [this.selectedOrigin, this.selectedDestination] = [this.selectedDestination, this.selectedOrigin];
      }

      this.waypoints = [...this.waypoints].reverse();
      this.lastSavedId = null;
      this.isCalculatingRoute = true;
    },

    calculateRoute(preserveView = false, optimize = false) {
    
      
      if (this.waypoints.length < 2) {
        this.routeError = this.$t('pv.generazione.route_error_min_stops');
        return;
      }

      this.routeError = '';
      this.isCalculatingRoute = true;
      this.optimizeRoute = optimize;
      this.$refs.routeMap?.calculateRoute?.(preserveView, optimize);
    },

    clearRoute() {
      this.$refs.routeMap?.clearRoute?.();
    },

    refreshRoute() {
      this.$refs.routeMap?.refresh?.();
    },

    reorderWaypoints(optimizedOrder) {
  
      let intermediateWaypoints = this.waypoints.slice(1, -1);
      let newIntermediate = [];

      optimizedOrder.forEach(index => {
        newIntermediate.push(intermediateWaypoints[index]);
      });

      this.waypoints = [
        this.waypoints[0],
        ...newIntermediate,
        this.waypoints[this.waypoints.length - 1]
      ];
    },

    initAutocomplete() {
      if (!this.$refs.newWaypointInput || !this.routingStrategy) return;

      this.routingStrategy.initAutocomplete(this.$refs.newWaypointInput, (place) => {
        this.tempPlace = place;
      });
    },

    initOriginAutocomplete() {
      const input = this.$refs.customOriginInput;
      if (!input || !this.routingStrategy) return;

      this.routingStrategy.initOriginAutocomplete(input, (predictions) => {
        this.originPredictions = predictions;
      });
    },




    openCustomDestinationInput() {
      this.showCustomDestinationInput = true;
    },

    initDestinationAutocomplete() {
      const input = this.$refs.customDestinationInput;
      if (!input || !this.routingStrategy) return;

      this.routingStrategy.initDestinationAutocomplete(input, (predictions) => {
        this.destinationPredictions = predictions;
      });
    },

    async selectOriginPrediction(prediction) {
      if (!this.routingStrategy) return;

      const result = await this.routingStrategy.selectPrediction(prediction);
      if (!result) {
        alert(this.$t('pv.generazione.alert_select_address_from_suggestions'));
        return;
      }

      this.customOrigins.push(result);
      this.selectedOrigin = this.customOrigins[this.customOrigins.length - 1];
      this.showCustomOriginInput = false;
      this.originPredictions = [];
    },


    async selectDestinationPrediction(prediction) {
      if (!this.routingStrategy) return;

      const result = await this.routingStrategy.selectPrediction(prediction);
      if (!result) {
        alert(this.$t('pv.generazione.alert_select_address_from_suggestions'));
        return;
      }

      this.customDestinations.push(result);
      this.selectedDestination = this.customDestinations[this.customDestinations.length - 1];
      this.showCustomDestinationInput = false;
      this.destinationPredictions = [];
    },


    addNewWaypoint() {
      // Se l'utente non ha cliccato sul menu a tendina, forza il recupero
      if (!this.tempPlace) {
        const value = this.$refs.newWaypointInput.value;
        if (!value) return;
        alert(this.$t('pv.generazione.alert_select_from_google_suggestions'));
        return;
      }

      if (!this.tempPlace.geometry) {
        alert(this.$t('pv.generazione.alert_no_geometry'));
        return;
      }

      const newWp = {
        id: 'manual_' + Date.now(),
        address: this.tempPlace.formatted_address,
        companyName: this.tempPlace.name || this.$t('pv.shared.new_waypoint'),
        lat: this.tempPlace.geometry.location.lat(),
        lng: this.tempPlace.geometry.location.lng(),
        notes: this.$t('pv.generazione.manual_waypoint_note'),
        telephon: '',
        avvertimenti: '',
        colli: 0,
        rimes: '',
        mdp: '',
        isManual: true,
        documents_data: []
      };

      // Inserimento prima dell'ultimo elemento (Arrivo)
      if (this.waypoints.length > 0) {
        const lastIndex = this.waypoints.length - 1;
        this.waypoints.splice(lastIndex, 0, newWp);
      } else {
        this.waypoints.push(newWp);
      }

      // Reset
      this.$refs.newWaypointInput.value = '';
      this.tempPlace = null;
      this.calculateRoute(true);
    },

    
    onDragEnd() {
      this.refreshRoute();
      this.syncTripsOrder(); 
    },
    

    syncTripsOrder() {
      let newOrderedTrips = [];
      const processedIds = new Set();

      this.waypoints.forEach(wp => {
          if (wp.id === 'origin' || wp.id === 'destination') return;

          if (wp.isManual) {
              if (!processedIds.has(wp.id)) {
                  const existingManual = this.trips.find(t => t.id === wp.id);
                  if (existingManual) {
                      newOrderedTrips.push(existingManual);
                  } else {
                      newOrderedTrips.push({
                          id: wp.id,
                          companyName: wp.companyName,
                          shippingAddress: wp.address,
                          isManual: true,
                          telephon: wp.telephon || '',
                          avvertimenti: wp.avvertimenti || '',
                          colli: wp.colli || 0,
                          rimes: wp.rimes || '',
                          mdp: wp.mdp || '',
                          num: '',
                          tipo_doc: null,
                      });
                  }
                  processedIds.add(wp.id);
              }
              return;
          }

          const ids = wp.allIds || [wp.id];
          
          ids.forEach(id => {
              const trip = this.trips.find(t => t.id === id);
              if (trip && !processedIds.has(id)) {
                  newOrderedTrips.push(trip);
                  processedIds.add(id);
              }
          });
      });

      this.trips.forEach(t => {
          if (!processedIds.has(t.id)) {
              newOrderedTrips.push(t);
          }
      });

      this.trips = newOrderedTrips;
    },


    applyRouteOrder() {
      // 1. Sincronizziamo l'ordine della lista principale (senza accorparla visivamente)
      this.syncTripsOrder();

      // 2. Procediamo al salvataggio su DB
      this.storeRoute();
    },

    async storeRoute() {
      if (!this._routePayload) {
        alert(this.$t('pv.generazione.alert_calc_route_first'));
        return;
      }

      this.isLoading = true;

      const mapsLink = this.routingStrategy
        ? this.routingStrategy.buildDeepLink(this.waypoints)
        : null;

      if (this.travelName === '') {
        this.travelName = this.$t('pv.generazione.default_travel_name', { carrier: this.selectedCarrier || '', date: new Date().toLocaleDateString('it-IT') });
      }
      this.generatedMapsLink = mapsLink;

      let polyline = '';
      if (this._routePayload.provider === 'google' && this._routePayload.raw?.routes?.[0]?.overview_polyline) {
        polyline = this._routePayload.raw.routes[0].overview_polyline;
      } else if (this._routePayload.provider === 'here' && this._routePayload.raw?.sections?.[0]?.polyline) {
        polyline = this._routePayload.raw.sections[0].polyline;
      }

      const payload = {
        carrier_id: this.carrierInfo?.id || this.selectedCarrier || null,
        travel_date: new Date().toISOString().split('T')[0],
        travel_name: this.travelName,
        origin_address: this.selectedOrigin?.address,
        destination_address: this.selectedDestination?.address,
        maps_link: mapsLink,
        travel_options: {
          avoid_tolls: this.avoidTolls,
          avoid_highways: this.avoidHighways,
          avoid_ferries: this.avoidFerries,
          optimize_route: this.optimizeRoute
        },        
        route_info: {
          distance: this.routeData.distance, 
          duration: this.routeData.duration, 
          polyline: polyline
        },
        stops: this.waypoints.filter(wp => wp.id !== 'origin' && wp.id !== 'destination').map((wp, index) => {
          return {
            sort_order: index + 1,
            documents_data: (wp.documents_data && wp.documents_data.length > 0) 
              ? wp.documents_data.map(d => ({ ...d, telephon: d.telephon || wp.telephon || '' }))
              : [{ telephon: wp.telephon || '' }],
            is_manual: wp.id.toString().startsWith('manual'),
            company_name: wp.companyName,
            address: wp.address,
            lat: wp.lat || wp.latitude || null,
            lng: wp.lng || wp.longitude || null,
            notes: wp.notes || '',
            avvertimenti: wp.avvertimenti || '',
            colli: wp.colli || 0,
            rimes: wp.rimes || '',
            mdp: wp.mdp || ''
          };
        })
      };
      
      try {
        
        const response = await axios.post(route('modules.percorsi-vettori.save'), payload);
        
        if (response.data.id) {
          this.lastSavedId = response.data.id;
          alert(this.$t('pv.generazione.alert_route_saved'));
          this.showRouteEditor = false;
        }
      } catch (error) {
        console.error("Errore salvataggio:", error);
        const msg = error.response?.data?.error || this.$t('pv.generazione.alert_route_save_default_error');
        alert(msg);
      } finally {
        this.isLoading = false;
      }
      
    
    },

    formatDate(date) {
      if (!date) return '';
      const d = new Date(date);
      if (isNaN(d.getTime())) return '';
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },

    formatDisplayDate(date) {
      if (!date) return '-';
      const d = new Date(date);
      return d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
    },

    formatDuration(seconds) {
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      if (hours > 0) {
        return `${hours}h ${minutes}m`;
      }
      return `${minutes} min`;
    },

    getDocType(tipo) {
      if (tipo == this.MagoEnums.Document_Type.Return_From_Customer) return 'RESO';
      if (tipo == this.MagoEnums.Document_Type.Delivery_Note) return 'DDT';
      if (tipo == this.MagoEnums.Document_Type.Picking_List) return 'PICKING';
      return 'C9';
    },

    async geocodeWaypoints() {
      if (!this.routingStrategy) return;
      await this.routingStrategy.geocodeWaypoints(this.waypoints);
    },

    async geocodeAndSetCenter(address) {
      if (!this.routingStrategy) return;

      const coords = await this.routingStrategy.geocodeAddress(address);
      if (coords) {
        this.mapCenter = {
          lat: coords.lat,
          lng: coords.lng
        };
      }
    },

    onAdjacentDuplicatesDetected(duplicates) {
      this.pendingDuplicates = duplicates;
      this.showDuplicatesModal = true;
    },

    mergeDuplicateWaypoints() {
      if (!this.pendingDuplicates || this.pendingDuplicates.length === 0) return;

      this.pendingDuplicates.forEach(group => {
        const toMerge = this.waypoints.slice(group.start, group.end + 1);
        const merged = {
          ...toMerge[0],
          allIds: toMerge.flatMap(wp => wp.allIds || [wp.id]),
          allCli: [...new Set(toMerge.flatMap(wp => wp.allCli || []))],
          allCliSede: [...new Set(toMerge.flatMap(wp => wp.allCliSede || []))],
          documents_data: toMerge.flatMap(wp => wp.documents_data || []),
          colli: toMerge.reduce((sum, wp) => sum + (parseInt(wp.colli) || 0), 0),
          rimes: [...new Set(toMerge.map(wp => wp.rimes).filter(Boolean))].join(' | '),
          mdp: [...new Set(toMerge.map(wp => wp.mdp).filter(Boolean))].join(' | '),
          notes: [...new Set(toMerge.map(wp => wp.notes).filter(Boolean))].join(' | '),
          telephon: [...new Set(toMerge.map(wp => wp.telephon).filter(Boolean))].join(' | '),
          avvertimenti: [...new Set(toMerge.map(wp => wp.avvertimenti).filter(Boolean))].join(' | ')
        };

        this.waypoints.splice(group.start, toMerge.length, merged);
      });

      this.mergeSameAddress = true;
      this.lastSavedId = null;
      this.showDuplicatesModal = false;
      this.pendingDuplicates = null;
      this.$nextTick(() => {
        this.calculateRoute();
      });
    },

    closeDuplicatesModal() {
      this.showDuplicatesModal = false;
      this.pendingDuplicates = null;
    },

    isDdtGenerating(pickingId) {
      return this.generatingDdtIds.includes(String(pickingId));
    },

    printDdt(docNo) {
      if (!docNo) return;
      const printUrl = route('modules.percorsi-vettori.ddt.print', { doc_no: docNo });
      window.open(printUrl, '_blank');
    },

    async generateDdt(pickingId) {
      const id = String(pickingId);
      if (!this.lastSavedId || this.isDdtGenerating(id)) return;

      this.ddtErrors[id] = '';
      this.generatingDdtIds.push(id);

      try {
        const response = await axios.post(route('modules.percorsi-vettori.ddt.generate'), {
          picking_ids: [id],
        });
        const created = (response.data?.created || []).find(group =>
          (group.picking_ids || []).some(createdId => String(createdId) === id)
        );

        if (created) {
          this.trips.forEach(trip => {
            if (String(trip.id) === id) {
              trip.delivered = 1;
              trip.ddtDocNo = created.doc_no || null;
            }
          });
          return;
        }

        const errors = (response.data?.failed || [])
          .filter(group => (group.picking_ids || []).some(failedId => String(failedId) === id))
          .flatMap(group => group.errors || []);
        this.ddtErrors[id] = errors.join(' | ') || 'Il DDT non è stato generato.';
      } catch (error) {
        this.ddtErrors[id] = error.response?.data?.message
          || error.response?.data?.error
          || 'Errore durante la generazione del DDT.';
      } finally {
        this.generatingDdtIds = this.generatingDdtIds.filter(generatingId => generatingId !== id);
      }
    },

    async esportaCsv() {
      if (!this.lastSavedId) {
        alert(this.$t('pv.generazione.alert_save_before_export'));
        return;
      }
      const exportUrl = route('modules.percorsi-vettori.export', this.lastSavedId);
      window.open(exportUrl, '_blank');
    },

    async printTable() {
      if (!this.lastSavedId) return;

      const printUrl = route('modules.percorsi-vettori.print', this.lastSavedId);
      window.open(printUrl, '_blank');
    }
  },
  watch: {
    routing_provider(newVal) {
      this.routingStrategy = createRoutingStrategy(newVal, this.routing_provider_key);
      this.preloadApi();
    },

    showCustomOriginInput(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          this.initOriginAutocomplete();
        });
      } else {
        this.originPredictions = [];
      }
    },

    showCustomDestinationInput(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          this.initDestinationAutocomplete();
        });
      } else {
        this.destinationPredictions = [];
        if (this.$refs.customDestinationInput) {
          this.$refs.customDestinationInput.value = '';
        }
      }
    },
    showRouteEditor(val) {
      if (val) {
        this.$nextTick(() => {
          this.initAutocomplete();
        });
      }
    },

    travelName() { this.lastSavedId = null; },

    mergeSameAddress() { this.lastSavedId = null; },

    selectedOrigin(val) {
      this.lastSavedId = null; 
    },

    selectedDestination() {
      this.lastSavedId = null;
    },

    

  },
  mounted() {
    this.routingStrategy = createRoutingStrategy(this.routing_provider, this.routing_provider_key);
    this.preloadApi();

    console.log("aaaaaaaaaaaaaaaa", this.veicoli);

    if (this.originOptions.length > 0) {
        this.selectedOrigin = this.originOptions[0];
    }

    if (this.companyInfo && this.companyInfo.length > 0) {
      const main = this.companyInfo[0];
      const fullAddress = `${main.Address}, ${main.City}`;
      this.geocodeAndSetCenter(fullAddress);
    }

    if (this.destinationOptions.length > 0) {
      this.selectedDestination = this.destinationOptions[0];
    }

    if (this.preloadedTrips && this.preloadedTrips.length > 0) {
      this.trips = [...this.preloadedTrips];
      this.hasSearched = true;
      this.showSelectionModal = false;
      this.mergeSameAddress = this.group_by_companyName_and_address;
      
      // Se c'è un unico vettore negli ordini precaricati, selezionalo
      const carriers = [...new Set(this.preloadedTrips.map(t => t.vettore))].filter(Boolean);
      if (carriers.length === 1) {
        this.selectedCarrier = carriers[0];
        // Cerchiamo l'oggetto carrier completo per popolare carrierInfo
        const fullCarrier = this.carriers.find(c => c.Carrier === this.selectedCarrier);
        if (fullCarrier) {
          this.carrierInfo = fullCarrier;
        }
      } else if (carriers.length > 1) {
        // Se ci sono più vettori, lasciamo che l'utente scelga o mettiamo un placeholder
        console.warn("Ordini con vettori multipli selezionati");
      }
    }
  },
};
</script>

<style>
.vue-select-style .vs__dropdown-toggle {
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  padding: 0.5rem;
}

.pac-container {
    z-index: 99999 !important;
    pointer-events: auto !important;
}

.vue-select-style .vs__dropdown-toggle:focus-within {
  border-color: #3b82f6;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
}

.vue-select-style .vs__search {
  margin: 0;
  padding: 0;
}

.vue-select-style .vs__selected {
  margin: 0;
}


</style>

<style scoped>
:deep(.drag-ghost) {
  opacity: 0 !important;
  height: 3px !important;
  background: #3b82f6 !important;
  border-radius: 2px !important;
  border: none !important;
  padding: 0 !important;
  overflow: hidden !important;
}

:deep(.drag-chosen) {
  opacity: 1 !important;
  box-shadow: 0 8px 24px rgba(59, 130, 246, 0.25) !important;
  transform: scale(1.02);
  z-index: 50;
}
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from, .fade-leave-to {
  opacity: 0;
}


</style>
